import { useState, useEffect } from "react";
import {
  getTopSoldProducts,
  getTopSpendingUsers,
} from "utils/api/private/reports.api";
import {
  ProductPerformance,
  ReportQuery,
  UserSpendingPerformance,
} from "utils/types/reports.types";
import { authToken, ROLES } from "utils/constant";

// MUI Imports
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Alert,
  TextField,
} from "@mui/material";

// Chart.js Imports
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useAuth } from "utils/context";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type Period = "year" | "month" | "week";

export const BiDashboard = () => {
  const [topProducts, setTopProducts] = useState<ProductPerformance[]>([]);
  const [topUsers, setTopUsers] = useState<UserSpendingPerformance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, ] = useState<Period>("year");
  const [filters, setFilters] = useState<ReportQuery>({
    period: "year",
    limit: 10,
  });
  const { user } = useAuth();
  useEffect(() => {
    const fetchReports = async () => {
      const token = localStorage.getItem(authToken);
      if (!token) {
        setError("Authentication failed.");
        return;
      }
      try {
        setIsLoading(true);
        setError(null);
        const [productsData, usersData] = await Promise.all([
          getTopSoldProducts(token, filters),
          getTopSpendingUsers(token, filters),
        ]);
        setTopProducts(productsData);
        setTopUsers(usersData);
      } catch (err: any) {
        setError(err.message || "Failed to load report data.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchReports();
  }, [filters]);

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value || undefined }));
  };

  const handleSelectChange = (event: SelectChangeEvent<Period>) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value as Period }));
  };


  const productChartData = {
    labels: topProducts.slice(0, 10).map((p) => p.productName), // Afficher les 10 premiers
    datasets: [
      {
        label: "Revenu Total (USD)",
        data: topProducts.slice(0, 10).map((p) => p.totalRevenueUSD),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: `Top 10 des Produits les Plus Vendus par Revenu (${period})`,
      },
    },
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <div>
     
      <Box>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select
                  name="period"
                  value={filters.period || "year"}
                  label="Period"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="week">Last 7 Days</MenuItem>
                  <MenuItem value="month">Last 30 Days</MenuItem>
                  <MenuItem value="year">Last Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(user?.role === ROLES.ADMIN || user?.role === ROLES.MANAGER) && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  name="country"
                  label="Country Code (e.g., CD)"
                  value={filters.country || ""}
                  onChange={handleTextFieldChange}
                  size="small"
                  fullWidth
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                name="userId"
                label="Filter by User ID"
                value={filters.userId || ""}
                onChange={handleTextFieldChange}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>
        <Grid container spacing={3}>
          {/* Graphique des produits les plus vendus */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, height: "400px" }}>
              <Bar data={productChartData} options={chartOptions} />
            </Paper>
          </Grid>

          {/* Tableau des utilisateurs qui dépensent le plus */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Top 10 des Meilleurs Clients
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nom d'utilisateur</TableCell>
                      <TableCell align="right">Total Dépensé (USD)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topUsers.slice(0, 10).map((u) => (
                      <TableRow hover key={u.userId}>
                        <TableCell>{u.username}</TableCell>
                        <TableCell align="right">
                          ${u.totalSpentUSD.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Tableau détaillé des produits les plus vendus */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Détail des Produits les Plus Vendus
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Revenu (USD)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topProducts.slice(0, 10).map((p) => (
                      <TableRow hover key={p.productId}>
                        <TableCell>{p.productName}</TableCell>
                        <TableCell align="right">
                          {p.totalQuantitySold}
                        </TableCell>
                        <TableCell align="right">
                          ${p.totalRevenueUSD.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};
